import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/css_pages/ServiceProgressForm.css';
import config from "../../config";
import Cookies from "js-cookie";
import PopupMessage from '../layouts/PopupMessage';
import Select from 'react-select';

const getcurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0'); 
  return `${year}-${month}`;
};

function ServiceProgressForm() {
  const token = Cookies.get("authToken");
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    month: getcurrentMonth(),
    tso_id: '',
    pendingTasks: '',
    meetingAttendance: '',
    trainingAttendance: '',
    aptitudeTest: ''
  });

  const [tsos, setTsos] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [error, setError] = useState('');
  
  const [isTsosLoading, setIsTsosLoading] = useState(true); // For initial TSO list fetch
  const [isProgressLoading, setIsProgressLoading] = useState(false); // For fetching progress data
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Open popup when component mounts
  useEffect(() => {
    const fetchTsos = async () => {
      try {
        setIsTsosLoading(true);
        setError('');
        const response = await fetch(`${config.API_URL}/tso/get-all`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
        if (!response.ok) throw new Error('Failed to fetch TSOs');
        const data = await response.json();
        setTsos(data);
        setIsTsosLoading(false);
      } catch (err) {
        console.error("Error fetching TSOs:", err);
        setError('Could not load TSO list. Please try again later.');
      } finally {
        setIsTsosLoading(false);
      }
    };

    fetchTsos();
  }, []);

  const tsoOptions = useMemo(() => 
    tsos.map(tso => ({
      value: tso.id,
      label: `${tso.name} (${tso.emp_no})`
    })), [tsos]);

  useEffect(() => {
    const fetchProgressData = async () => {
      if (formData.tso_id && formData.month) {
        setIsProgressLoading(true); 
        setError('');
        try {
          const response = await fetch(`${config.API_URL}/tso/get-tso-monthly-progress/${formData.tso_id}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!response.ok) throw new Error('Failed to fetch progress data');
          const allProgress = await response.json();
          const existingRecord = allProgress.find(p => p.month === formData.month);

          if (existingRecord) {
            // Record exists, populate form and set to update mode
            setFormData(prev => ({
              ...prev,
              pendingTasks: existingRecord.no_of_pending_tasks,
              meetingAttendance: existingRecord.meeting_attendance,
              trainingAttendance: existingRecord.training_attendance,
              aptitudeTest: existingRecord.aptitude_test,
            }));
            setIsUpdateMode(true);
          } else {
            // No record, reset fields and set to save mode
            setFormData(prev => ({
              ...prev,
              pendingTasks: '',
              meetingAttendance: '',
              trainingAttendance: '',
              aptitudeTest: '',
            }));
            setIsUpdateMode(false);
          }
        } catch (err) {
          console.error("Error fetching progress data:", err);
          setError('Could not fetch progress data.');
          setIsUpdateMode(false);
        } finally {
          setIsProgressLoading(false);
        }
      }
    };

    fetchProgressData();
  }, [formData.tso_id, formData.month, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTsoChange = (selectedOption) => {
    setFormData({
      ...formData,
      tso_id: selectedOption ? selectedOption.value : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Map frontend state to API payload
    const payload = {
      tso_id: formData.tso_id,
      month: formData.month,
      no_of_pending_tasks: formData.pendingTasks,
      meeting_attendance: formData.meetingAttendance,
      training_attendance: formData.trainingAttendance,
      aptitude_test: formData.aptitudeTest,
    };
    console.log("Submitting payload:", payload);

    try {
      const response = await fetch(`${config.API_URL}/tso/monthly-progress-upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) throw new Error(result.error || 'Failed to save data');
      console.log('Form submitted:', result.message);
      setIsPopupOpen(false);
      navigate('/customer-service-progress');
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsPopupOpen(false);
    navigate('/customer-service-progress');
  };

  let areFieldsDisabled = !formData.tso_id || !formData.month;

  return (
    <div className="app">
      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-header">
              <h2>Service Progress Form</h2>
              <button 
                className="close-btn"
                onClick={handleClose}
              >
                &times;
              </button>
            </div>

            {/* --- Step 2: Conditionally render based on loading states --- */}
            {isTsosLoading ? (
              <div className="loading-container">
                <p className="success-message">Loading...</p>
              </div>
            ) : (
              <form className="service-form" onSubmit={handleSubmit}>
                {error && <p className="error-message">{error}</p>}

                <div className="form-group">
                  <label htmlFor="tso_id">TSO</label>
                  <Select
                    id="tso_id"
                    name="tso_id"
                    options={tsoOptions}
                    value={tsoOptions.find(option => option.value === formData.tso_id)}
                    onChange={handleTsoChange}
                    isLoading={isTsosLoading}
                    isClearable
                    isSearchable
                    placeholder="Type to search or select a TSO"
                    inputId="tso_id_input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="month">Month</label>
                  <input
                    type="month"
                    id="month"
                    name="month"
                    value={formData.month}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                {isProgressLoading ? (
                  <div className="loading-container">
                    <p className="success-message">Loading...</p>
                  </div>
                ) : (
                  <>
                  <div className="form-group">
                    <label htmlFor="pendingTasks">No of Pending Tasks</label>
                    <input
                      type="number"
                      id="pendingTasks"
                      name="pendingTasks"
                      value={formData.pendingTasks}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="Enter number of pending tasks"
                      required
                      disabled={areFieldsDisabled}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="meetingAttendance">Meeting Attendance (%)</label>
                    <input
                      type="number"
                      id="meetingAttendance"
                      name="meetingAttendance"
                      value={formData.meetingAttendance}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      placeholder="0-100"
                      required
                      disabled={areFieldsDisabled}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="trainingAttendance">Training Attendance (%)</label>
                    <input
                      type="number"
                      id="trainingAttendance"
                      name="trainingAttendance"
                      value={formData.trainingAttendance}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      placeholder="0-100"
                      required
                      disabled={areFieldsDisabled}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="aptitudeTest">Aptitude Test Score</label>
                    <input
                      type="number"
                      id="aptitudeTest"
                      name="aptitudeTest"
                      value={formData.aptitudeTest}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      placeholder="0-100"
                      required
                      disabled={areFieldsDisabled}
                    />
                  </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="submit" className="submit-btn" disabled={isSubmitting || isProgressLoading || areFieldsDisabled}>
                    {isSubmitting ? 'Saving...' : (isUpdateMode ? 'Update' : 'Save')}
                  </button>
                  <button type="button" className="cancel-btn" onClick={handleClose}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceProgressForm;